import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useMutation, useLazyQuery, useQuery } from '@apollo/client';
import {
  GET_TICKET_FORM_SYSTRAY,
  SAVE_TICKET,
  SystrayClient,
  SystrayAccountClient,
  GET_FIELD
} from '@nile/graphql';
import { GET_CLIENT_USER_INFO } from '@nile/itsm/src/graphql/itsm-queries';
import { withRouter } from 'react-router-dom';
import { dompurifier } from '@nile/components/src/Utils/helpers';
import {
  ButtonGroup,
  Button,
  Page,
  Heading,
  TextField
} from '@superops/ui-design-system';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  getTranslatedText,
  TEXT_LENGTH,
  toast,
  validateForm,
  ERROR_CODES,
  Editor,
  Loader,
  useProduct,
  COMMON_CONSTANTS,
  fetchApi,
  toast as niletoast,
  AccessControl
} from '@nile/components';
import {
  cloneDeep,
  filter,
  forEach,
  isEmpty,
  map,
  omit,
  debounce
} from 'lodash';
import styled from 'styled-components';
import { SVGIcon } from '@superops/super-icons';
import { CustomFormTemplate } from '@nile/mangifera/src/Containers/FormContainers';
import envConfig from '@nile/home/src/Config/env';
import SiteSelect from '@nile/projects/src/Components/Inputs/SiteSelect';
import { DepartmentSelect } from '@nile/itsm/src/pages/components/DepartmentSelect';
import { useGetDefaultTicketType } from '@nile/mangifera/src/Utils/Hooks';
import { takeSnapShot } from './SystemTrayChatPage/utils/screenshot';
import { getScreenshotAttachmentResp } from './SystemTrayChatPage/helper';
import { loggerInfo } from '@nile/home/src/coralogixRUM';

const Wrapper = styled.div`
  .react-grid-layout {
    border: none;
    width: 100% !important;
  }
  .value_selector__control {
    min-width: 160px;
  }
  .value_selector__menu {
    z-index: 21;
  }
`;

const SysTrayNewTicketPage = (props) => {
  return (
    <div className="full-height">
      <WrappedComponents {...props} />
    </div>
  );
};

const WrappedComponents = (data) => {
  const { intl, eventData, ticket } = data;
  const { TicketFormId: ticketFormId, AppAcc: clientId } = eventData || {};

  const { defaultTicketType } = useGetDefaultTicketType({
    client: SystrayClient
  });

  const editorRef = useRef();
  const isMSPProduct = useProduct(COMMON_CONSTANTS.MSP);
  const [ticketData, setTicketData] = useState({
    ticketType: defaultTicketType || 'Incident'
  });
  const [templateId, setTemplateId] = useState(ticketFormId);

  const [errors, setErrors] = useState();
  const [submitActionLoading, setSubmitActionLoading] = useState(false);
  const [template, setTemplate] = useState({
    name: '',
    content: '',
    placeholdersUsed: '',
    attachments: []
  });
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [selectedFormTemplate, updateSelectedFormTemplate] = useState(null);
  const [allFormFields, setAllFormFields] = useState([]);
  const [dependantFieldEnabled, setDependantFieldEnabled] = useState(false);
  const [mandatoryFieldsOnCreation, setMandatoryFieldsOnCreation] = useState(
    []
  );
  const [siteOptions, setSiteOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);

  const headerContext = {
    headers: {
      'asset-id': eventData?.AssetId,
      'asset-token': eventData?.AssetToken,
      'app-acc': eventData?.AppAcc,
      'X-SO-UA-Build-Version': '1.7',
      reqType: 'assetUser'
    }
  };

  const [
    getClientUserInfo,
    { data: clientUserData, error: clientUserError }
  ] = useLazyQuery(GET_CLIENT_USER_INFO, {
    client: SystrayAccountClient,
    context: headerContext,
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
    skip: isMSPProduct,
    onError: () => {
      // Reset site and department options when API fails
      setSiteOptions([]);
      setDepartmentOptions([]);

      // Reset site and department in ticketData
      setTicketData((prevState) => ({
        ...prevState,
        site: undefined,
        department: undefined
      }));
    }
  });

  // Create a debounced version of getClientUserInfo
  const debouncedGetClientUserInfo = useCallback(
    debounce((email) => {
      if (email) {
        getClientUserInfo({
          variables: {
            input: {
              email: email
            }
          }
        });
      }
    }, 1000), // 500ms debounce time
    [getClientUserInfo]
  );

  const updateTemplate = (obj) => {
    const updatedTemplate = cloneDeep(template);
    setTemplate({ ...updatedTemplate, ...obj });
  };

  const setValuesToEmptyString = (obj) => {
    const newObj = cloneDeep(obj);
    Object.keys(newObj).forEach((key) => {
      if (
        typeof newObj[key] === 'object' &&
        (newObj[key] !== null || newObj[key] !== '')
      ) {
        setValuesToEmptyString(newObj[key]);
      } else {
        newObj[key] = '';
      }
    });
    return newObj;
  };

  const getTicketForm = async (formId) => {
    setFormLoading(true);
    await SystrayClient.query({
      query: GET_TICKET_FORM_SYSTRAY,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
      variables: {
        ticketFormId: formId,
        client: { accountId: clientId }
      },
      context: headerContext
    })
      .then((res) => {
        if (res?.data?.getTicketForm) {
          updateSelectedFormTemplate(res?.data?.getTicketForm);
          if (res?.data?.getTicketForm) {
            const fldDetails =
              (res?.data?.getTicketForm?.fieldDetails &&
                JSON.parse(res?.data?.getTicketForm?.fieldDetails)?.layout) ||
              [];
            if (!isEmpty(fldDetails)) {
              setAllFormFields(fldDetails);
              const isDependantFieldExist = fldDetails?.some(
                (el) => el?.field?.fieldProps?.parentField
              );
              // Default to true for systray when parentField is not available in API
              setDependantFieldEnabled(isDependantFieldExist || true);
            }
          }
        }
      })
      .finally(() => setFormLoading(false));
  };

  const clearData = () => {
    const dta = ticketData;
    const clearedValue = setValuesToEmptyString(dta);
    setTicketData(clearedValue);
    setTemplate({
      name: '',
      content: '',
      placeholdersUsed: '',
      attachments: []
    });
    getTicketForm(templateId);
    editorRef.current?.updateEditorContent('');
  };

  const [saveTickeForm, { loading }] = useMutation(SAVE_TICKET, {
    client: SystrayClient,
    context: headerContext,
    onCompleted({ saveTicket }) {
      if (saveTicket) {
        toast('success', 'app.toast.newTicket.creation.success');
        clearData();
      }
      setSubmitActionLoading(false);
    },
    onError(error) {
      clearData();
      const errorCode =
        error?.graphQLErrors?.[0]?.extensions?.clientError?.[0]?.errorCode;

      if (errorCode && ERROR_CODES[errorCode]) {
        // if (window.logger) {
        //   window.logger.info('System tray request error', {
        //     error
        //   });
        //   window.logger.flush();
        // }

        loggerInfo('System tray request error', { error });

        toast('error', getTranslatedText(intl, ERROR_CODES[errorCode]));
      } else {
        // if (window.logger) {
        //   window.logger.info('System tray request error', {
        //     error
        //   });
        //   window.logger.flush();
        // }

        loggerInfo('System tray request error', { error });

        toast('error', 'Something went wrong');
      }
      setSubmitActionLoading(false);
    }
  });

  const handleSubmit = () => {
    const inputParams = {
      ...ticketData,
      ticketType: defaultTicketType || 'Incident',
      description: template.content,
      conversation: {
        content: template.content,
        attachments:
          template.attachments.length > 0 ? template.attachments : null
      }
    };

    /* Remove department payload if it's MSP product
     * for MSP product, department is not required and the department field is not get updated
     * added for edge case if any
     */
    if (isMSPProduct && inputParams.department) {
      delete inputParams.department;
    }
    if (ticketData?.department?.businessFunctionId) {
      inputParams.department = {
        businessFunctionId: ticketData?.department?.businessFunctionId
      };
    }
    if (ticketData?.site) {
      inputParams.site = {
        id: ticketData?.site?.id
      };
    }

    setSubmitActionLoading((prevState) => !prevState);
    const newTicketConstraints = {
      subject: {
        presence: {
          allowEmpty: false,
          message: '^Enter ticket subject'
        }
      },
      'requester.email': {
        email: true,
        presence: {
          allowEmpty: false,
          message: '^Requester email must be provided'
        }
      }
    };
    const form = selectedFormTemplate || null;
    const currentFormLayout = form?.fieldDetails
      ? JSON.parse(form?.fieldDetails)?.layout
      : [];

    const allFields = currentFormLayout.map((el) => ({
      name: el?.field?.fieldProps?.label,
      columnName: el?.field?.fieldProps?.name,
      type: el?.field?.type
    }));

    const mandatoryFieldsOnCurrentForm = allFields?.filter((el) =>
      mandatoryFieldsOnCreation?.some((f) => f?.columnName === el?.columnName)
    );

    let ticketConstraints = { ...newTicketConstraints };

    mandatoryFieldsOnCurrentForm.forEach((el) => {
      const type = el?.type;
      const key =
        type === 'default' ? el.columnName : `customFields.${el.columnName}`;
      const newConstraint = {
        [key]: {
          presence: {
            allowEmpty: false,
            message: `^${el?.name} cannot be empty`
          }
        }
      };
      ticketConstraints = { ...ticketConstraints, ...newConstraint };
    });
    if (ticket?.ticketId) {
      ticket.id = ticket.ticketId;
    }
    if (ticket?.description) {
      ticket.description = dompurifier(template.content);
    }
    const sopId = selectedFormTemplate?.runbook?.sopId;

    if (selectedFormTemplate && sopId) {
      inputParams.sop = {
        sopId: sopId
      };
    }

    const formToValidate = cloneDeep(inputParams);
    const validationResult = validateForm(formToValidate, ticketConstraints);

    if (validationResult) {
      setErrors(validationResult);
      setSubmitActionLoading(false);
      return;
    }
    setErrors();

    saveTickeForm({
      variables: {
        ...inputParams,
        isMSPProduct
      }
    });
  };

  const getChilds = (field) => {
    const result = [];
    function findChildren(parentColumnName) {
      if (!parentColumnName) return;
      const children = filter(
        allFormFields,
        (fld) =>
          fld?.field?.fieldProps?.parentField?.columnName === parentColumnName
      );
      result.push(...children);
      forEach(children, (child) => findChildren(child?.fieldProps?.name));
    }
    findChildren(field);
    return result;
  };

  const updateField = (key, field) => {
    const childs = getChilds(key);
    let childreset;
    childs?.map(
      (el) =>
        (childreset = { ...childreset, [el?.field?.fieldProps?.name]: null })
    );
    setTicketData((prevState) => ({
      ...prevState,
      ...childreset,
      [key]: field
    }));

    // Use debounced function for email changes
    if (key === 'requester' && field.email && !isMSPProduct) {
      debouncedGetClientUserInfo(field.email);
    }
  };

  const handleFileAttachments = (files, callback) => {
    const formData = new FormData();
    // formData.append('files', files);
    files.forEach((file, idx) => {
      formData.append(`files`, file);
    });
    formData.append('module', 'TICKET_CONVERSATION_ATTACHMENT');
    const { reqType, ...rest } = headerContext.headers;
    const requestOptions = {
      method: 'POST',
      credentials: 'include',
      body: formData,
      headers: rest
    };
    setIsFileUploading(true);
    fetchApi(`${envConfig.fileRoot}/upload`, requestOptions)
      .then((response) => {
        return response.json();
      })
      .then((list) => {
        const updatedTemplate = cloneDeep(template);
        const updatedAttachments = list?.data?.map((el) =>
          omit(el, ['downloadURL'])
        );
        const updatedList = [
          ...updatedTemplate.attachments,
          ...(updatedAttachments || [])
        ];

        updatedTemplate.attachments = updatedList;
        setTemplate(updatedTemplate);
        setIsFileUploading(false);
      })
      .catch((err) => {
        console.error('Error:', err);
        setIsFileUploading(false);
      })
      .finally(() => {
        setIsFileUploading(false);
        if (callback) {
          callback();
        }
      });
  };

  const handleAttachmentsDeletion = (fileName) => {
    const newState = cloneDeep(template);
    const updatedList = newState.attachments.filter(
      (item) => item.fileName !== fileName
    );
    newState.attachments = updatedList;
    setTemplate(newState);
  };

  const updateTemplateId = (templateId) => {
    setTemplateId(templateId);
  };

  const takeSnap = () => {
    takeSnapShot({
      customHeaders: headerContext?.headers,
      formData: {
        module: 'TICKET_CONVERSATION_ATTACHMENT'
      }
    });
  };

  useEffect(() => {
    setTicketData(ticket);
  }, [ticket]);

  useEffect(() => {
    if (ticketFormId && templateId !== ticketFormId) {
      setTemplateId(ticketFormId);
    }
  }, [ticketFormId]);

  useEffect(() => {
    if (templateId) getTicketForm(templateId);
  }, [templateId]);

  useEffect(() => {
    if (clientUserData?.getClientUserInfo) {
      const { site, businessFunction } = clientUserData.getClientUserInfo;
      if (site?.id) {
        const formattedSite = {
          id: site.id,
          name: site.name,
          label: site.name
        };
        setSiteOptions([formattedSite]);
        updateField('site', { id: site.id, name: site.name });
      } else {
        // Reset site in ticketData when site ID is missing
        setSiteOptions([]);
        setTicketData((prevState) => ({
          ...prevState,
          site: undefined
        }));
      }

      if (businessFunction?.businessFunctionId) {
        const formattedDepartment = {
          id: businessFunction.businessFunctionId,
          value: businessFunction.businessFunctionId,
          name: businessFunction.name,
          label: businessFunction.name
        };
        setDepartmentOptions([formattedDepartment]);
        updateField('department', {
          businessFunctionId: businessFunction.businessFunctionId,
          name: businessFunction.name
        });
      } else {
        // Reset department in ticketData when business function ID is missing
        setDepartmentOptions([]);
        setTicketData((prevState) => ({
          ...prevState,
          department: undefined
        }));
      }
    } else if (clientUserError) {
      // Reset site and department options when API fails
      setSiteOptions([]);
      setDepartmentOptions([]);

      // Reset site and department in ticketData
      setTicketData((prevState) => ({
        ...prevState,
        site: undefined,
        department: undefined
      }));
    }
  }, [clientUserData, clientUserError]);

  useEffect(() => {
    window.addEventListener('message', (event) => {
      if (window.frameParentOrigin) {
        try {
          if (event?.data?.data?.screenshotResponse) {
            const isValidFormat = event?.data?.data?.isNewFormat || false;
            const { attachment, error } = getScreenshotAttachmentResp({
              data: event?.data?.data,
              validFormat: isValidFormat
            });

            if (!error) {
              const parsedAttachment = (
                attachment?.data ||
                attachment ||
                []
              )?.map((el) => omit(el, ['downloadURL']));
              setTemplate((prevState) => ({
                ...prevState,
                attachments: [
                  ...prevState?.attachments,
                  ...(parsedAttachment || [])
                ]
              }));
            } else {
              niletoast('error', error);
            }
          }
          if (event?.data?.data?.downloadRes) {
            const respErr = event?.data?.data?.downloadRes?.DownloadFileError;
            if (isEmpty(respErr)) {
              niletoast(
                'success',
                event?.data?.data?.downloadRes?.DownloadFileName
              );
            } else {
              niletoast(
                'error',
                event?.data?.data?.downloadRes?.DownloadFileError
              );
            }
          }
          if (event?.data?.data?.templateId) {
            updateTemplateId(event?.data?.data?.templateId);
          }
        } catch (err) {
          console.log(err, 'Err in processing wails message');
        }
      }
    });
  }, []);

  if (isEmpty(eventData?.AssetId)) {
    return (
      <div className="agentLoadingText">
        <span className="agentLoader" />
      </div>
    );
  }

  return (
    <Page
      variant="primary"
      contentHeight="100px"
      title={
        <Heading element="h1">
          {eventData?.pageTitle ? (
            eventData?.pageTitle
          ) : (
            <FormattedMessage id="app.create.ticket.new" />
          )}
        </Heading>
      }
      primaryAction={
        <ButtonGroup>
          <Button
            onClick={() => {
              setFormLoading(false);
              clearData();
            }}
            icon={<SVGIcon name="RotateRefreshLoading" />}
          />
          <Button
            loading={loading || submitActionLoading}
            primary
            onClick={() => {
              handleSubmit();
            }}
            icon={
              <div className="fx center">
                <SVGIcon name="Save" color="#ffffff" />
              </div>
            }
          >
            <span className="fx actr">
              <FormattedMessage id="common.save" />
            </span>
          </Button>
        </ButtonGroup>
      }
    >
      <Wrapper>
        <div className="center">
          <div className="flex actr g30 wrap">
            <div className="f21 min-w200">
              <TextField
                id="requestorEmail"
                value={ticketData?.requester?.email || ''}
                onChange={(value) => {
                  updateField('requester', { email: value });
                }}
                placeholder={getTranslatedText(intl, 'client.new.email.pl')}
                type="email"
                customClassName="Header-Input"
                error={errors?.['requester.email']?.[0]}
                maxLength={TEXT_LENGTH.size_5}
                noborder
              />
            </div>
            {/* Site and Department selectors */}

            <AccessControl
              productToCheck="IT"
              render={
                <div className="flex g10 mt10 mb10 f21">
                  {ticketData?.site?.id && (
                    <div className="w50">
                      <SiteSelect
                        updateSite={(val) => {}}
                        site={ticketData?.site}
                        disabled={Boolean(ticketData?.site)}
                        icon
                        disableApiCalls
                        prePopulateOptions={siteOptions}
                        roundedIcon
                        hideLabel
                      />
                    </div>
                  )}
                  {ticketData?.department?.businessFunctionId && (
                    <div className="w50">
                      <DepartmentSelect
                        showIcon
                        onChange={(val) => {}}
                        department={departmentOptions[0]}
                        disableApiCalls
                        prePopulateOptions={departmentOptions}
                        disabled={Boolean(ticketData?.department)}
                      />
                    </div>
                  )}
                </div>
              }
            />
          </div>

          <TextField
            id="ticketSubject"
            value={ticketData?.subject || ''}
            onChange={(value) => {
              updateField('subject', value);
            }}
            placeholder={getTranslatedText(intl, 'ticket.new.pl.subject')}
            type="text"
            customClassName="Header-Input"
            error={errors?.subject?.[0]}
            maxLength={TEXT_LENGTH.size_5}
            noborder
          />

          <div className="flex">
            <div className="mbt10">
              <Editor
                key={!!clientId}
                editorRef={editorRef}
                template={template}
                updateTemplate={updateTemplate}
                module="TICKET"
                config={{
                  heightMin: 200,
                  heightMax: 300
                }}
                placeholder={getTranslatedText(intl, 'ticket.new.pl.desc')}
                handleFileAttachments={handleFileAttachments}
                handleAttachmentsDeletion={handleAttachmentsDeletion}
                isUploading={isFileUploading}
                details={
                  clientId
                    ? {
                        operation: 'CREATE',
                        clientId: clientId
                      }
                    : { operation: 'CREATE' }
                }
                allowMultiple
                toolbarSticky={false}
                disableImageUpload
                enableScreenShot
                takeSnapshot={takeSnap}
              />
            </div>
          </div>

          {selectedFormTemplate ? (
            <>
              {formLoading ? (
                <div className="flex-center min-height-100">
                  <Loader />
                </div>
              ) : (
                <>
                  <div className="divider mbt5" />
                  <div className={selectedFormTemplate ? 'full-height p8' : ''}>
                    <CustomFormTemplate
                      ticket={ticketData}
                      selectedFormTemplate={selectedFormTemplate}
                      updateField={updateField}
                      contentHeight
                      mandatoryFieldsOnCreation={mandatoryFieldsOnCreation}
                      setMandatoryFieldsOnCreation={
                        setMandatoryFieldsOnCreation
                      }
                      updateSelectedFormTemplate={updateSelectedFormTemplate}
                      errors={errors}
                      headerContext={headerContext}
                      systray
                      dependantFieldEnabled={dependantFieldEnabled}
                    />
                  </div>
                </>
              )}
            </>
          ) : (
            ''
          )}
        </div>
      </Wrapper>
    </Page>
  );
};

export default injectIntl(withRouter(SysTrayNewTicketPage));
